import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, Trash2, CheckCircle2, Layers, ShieldCheck, Wand2, Sparkles } from "lucide-react";

export default function UploadResume() {
  const [busy, setBusy] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/resumes");
      setResumes(r.data || []);
      if (!active && r.data?.[0]) setActive(r.data[0]);
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File is larger than 5MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    toast.loading("Parsing your resume and scoring it…", { id: "upload" });
    try {
      const res = await api.post("/resumes/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        res.data.ats_score != null ? `Parsed · ATS ${res.data.ats_score}/100` : "Resume parsed",
        { id: "upload" }
      );
      setActive({
        resume_id: res.data.resume_id,
        filename: res.data.filename,
        parsed: res.data.parsed,
        ats_score: res.data.ats_score,
        ats_review: res.data.ats_review,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: "upload" });
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/resumes/${id}`);
      toast.success("Deleted");
      setActive(null);
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <div className="chip mb-3"><Sparkles size={12} /> Step 1</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Upload your resume</h1>
        <p className="text-slate-500 mt-2 max-w-xl text-lg">
          PDF or DOCX, up to 5MB. We extract your skills, roles, and keywords — and we never invent experience.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 fade-up-delay-1">
        <div className="lg:col-span-2 space-y-5">
          <label
            data-testid="resume-dropzone"
            className="block cursor-pointer rounded-3xl border-2 border-dashed border-white/15 bg-white/[0.03] hover:border-[#00E5FF] hover:bg-[rgba(0,229,255,0.06)] transition-colors p-12 text-center"
          >
            <input
              data-testid="resume-file-input"
              type="file"
              className="hidden"
              accept=".pdf,.docx"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <div className="grid place-items-center w-16 h-16 rounded-3xl bg-[rgba(157,0,255,0.14)] text-[#1E6FE6] mx-auto mb-4">
              <UploadCloud size={28} />
            </div>
            <div className="font-display font-bold text-xl">
              {busy ? "Parsing and scoring…" : "Drop your resume here, or click to browse"}
            </div>
            <div className="text-sm text-slate-500 mt-2">PDF or DOCX · 5MB max · We auto-run an ATS review</div>
          </label>

          {active && (
            <div className="glass p-6" data-testid="parsed-output">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[rgba(128,255,234,0.1)] text-emerald-600">
                    <CheckCircle2 size={20} />
                  </span>
                  <div>
                    <div className="font-display font-bold text-lg">{active.filename}</div>
                    <div className="text-xs text-slate-500">Parsed successfully</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {active.ats_score != null && <AtsBadge score={active.ats_score} />}
                  <button
                    onClick={() => removeOne(active.resume_id)}
                    data-testid="delete-active-resume"
                    className="grid place-items-center w-9 h-9 rounded-xl text-slate-500 hover:bg-[rgba(255,0,127,0.1)] hover:text-[#FF6DAE] transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <ParsedPreview parsed={active.parsed} />

              {/* Next-action CTAs */}
              <div className="mt-6 pt-6 border-t border-white/8">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">What&apos;s next</div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <NextCta
                    to="/versions"
                    icon={Layers}
                    title="Generate 3 versions"
                    sub="ATS · Modern · One-page"
                    color="#F59E0B"
                    bg="#FEF3C7"
                    testid="cta-generate-versions"
                  />
                  <NextCta
                    to="/ats"
                    icon={ShieldCheck}
                    title="See full ATS report"
                    sub="Issues, fixes & strengths"
                    color="#10B981"
                    bg="#D1FAE5"
                    testid="cta-full-ats"
                  />
                  <NextCta
                    to="/tailor"
                    icon={Wand2}
                    title="Tailor to a JD"
                    sub="Cover letter included"
                    color="#1E6FE6"
                    bg="#E6F0FF"
                    testid="cta-tailor"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="glass p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Your resumes</div>
            {resumes.length === 0 && <div className="text-sm text-slate-500">No resumes yet.</div>}
            <ul className="space-y-2">
              {resumes.map((r) => (
                <li
                  key={r.resume_id}
                  className={`rounded-2xl border p-3 cursor-pointer transition-colors ${
                    active?.resume_id === r.resume_id ? "border-[#00E5FF] bg-[rgba(0,229,255,0.08)]" : "border-white/10 hover:bg-white/5"
                  }`}
                  onClick={() => setActive(r)}
                  data-testid={`resume-item-${r.resume_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-slate-500 shrink-0" />
                      <div className="text-sm font-semibold truncate">{r.filename}</div>
                    </div>
                    {r.ats_score != null && (
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{
                          background: r.ats_score >= 70 ? "#D1FAE5" : r.ats_score >= 40 ? "#FEF3C7" : "#FEE2E2",
                          color: r.ats_score >= 70 ? "#047857" : r.ats_score >= 40 ? "#B45309" : "#B91C1C",
                        }}
                      >
                        {r.ats_score}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 truncate">{r.parsed?.current_title || "—"}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function AtsBadge({ score }) {
  const color = score >= 70 ? "#10B981" : score >= 40 ? "#F59E0B" : "#EF4444";
  const bg = score >= 70 ? "#D1FAE5" : score >= 40 ? "#FEF3C7" : "#FEE2E2";
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl" style={{ background: bg }}>
      <ShieldCheck size={14} style={{ color }} />
      <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>
        ATS {score}/100
      </span>
    </div>
  );
}

function NextCta({ to, icon: Icon, title, sub, color, bg, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="glass p-4 hover-lift block group"
    >
      <span className="grid place-items-center w-10 h-10 rounded-2xl mb-2.5 transition-transform group-hover:scale-110" style={{ background: bg, color }}>
        <Icon size={16} />
      </span>
      <div className="font-display font-bold text-sm">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </Link>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[rgba(157,0,255,0.14)] text-[#E4CBFF] text-xs font-semibold">
      {children}
    </span>
  );
}

export function ParsedPreview({ parsed }) {
  if (!parsed) return null;
  return (
    <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
      <Info label="Name" value={parsed.name} />
      <Info label="Title" value={parsed.current_title} />
      <Info label="Email" value={parsed.email} />
      <Info label="Phone" value={parsed.phone} />
      <Info label="Location" value={parsed.location} />
      <Info label="LinkedIn" value={parsed.linkedin} />
      <Info label="Years of experience" value={parsed.years_of_experience} />
      <Info label="Certifications" value={(parsed.certifications || []).join(", ")} />
      <div className="sm:col-span-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Summary</div>
        <p className="text-slate-200 mt-1 leading-relaxed">{parsed.summary || "—"}</p>
      </div>
      <div className="sm:col-span-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Skills</div>
        <div className="flex flex-wrap gap-2">
          {(parsed.skills || []).slice(0, 30).map((s) => <Chip key={s}>{s}</Chip>)}
          {!parsed.skills?.length && <span className="text-slate-500 text-sm">—</span>}
        </div>
      </div>
      <div className="sm:col-span-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Keywords</div>
        <div className="flex flex-wrap gap-2">
          {(parsed.keywords || []).slice(0, 30).map((s) => (
            <span key={s} className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/[0.06] text-slate-200 text-xs font-semibold">
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-white font-medium mt-0.5 break-all">{value || "—"}</div>
    </div>
  );
}
